<script lang="ts" setup>
  import {
    XIcon,
    ThumbsDownIcon,
    ThumbsUpIcon,
    PanelRightOpenIcon,
    PanelRightCloseIcon,
    FileIcon,
    FileTextIcon,
    UsersIcon,
    PlusIcon,
    DownloadIcon,
    InfoIcon,
    UploadIcon,
    UserIcon,
    BuildingIcon
  } from 'lucide-vue-next'
  import { inject, ref, watch, nextTick, computed, onMounted, type Ref, provide } from 'vue'
  import type {
    AIChatSession,
    CustomerCase,
    CustomerOrganisationUser,
    CustomerUser
  } from '@/types/generated'
  import { EntityTypeEnum, SessionStateEnum } from '@/types/generated'
  import AiChatMessage from '@/components/app/cases/chat/AiChatMessage.vue'
  import AiChatMessageInput from '@/components/app/cases/chat/AiChatMessageInput.vue'
  import SkeletonLoader from '@/components/skeleton/SkeletonLoader.vue'
  import DocumentUpload from '@/components/app/cases/documents/DocumentUpload.vue'
  import OffCanvas from '@/components/ui/OffCanvas.vue'
  import UserAvatar from '@/components/app/users/UserAvatar.vue'
  import DocumentsPopover from '@/components/app/cases/chat/DocumentsPopover.vue'
  import ParticipantsPopover from '@/components/app/cases/chat/ParticipantsPopover.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useEntityCard } from '@/composables/useEntityCard'

  const { t } = useTranslation({
    en: {
      ChatDescription: 'No messages',
      Document: 'Document',
      Documents: 'Documents',
      Participant: 'Party',
      Participants: 'Parties',
      Ready: 'Ready',
      UpdatingContext: 'Updating context',
      SearchingDocuments: 'Searching documents',
      Thinking: 'Thinking',
      Hello: 'Hello',
      HowCanIHelpYou: 'How can I help you?',
      ChooseQuestionTemplate:
        'Choose one of the following question templates or ask your own question.',
      Page: 'Page',
      Pages: 'Pages',
      KIChatHasAccessToAllDocumentsInTheRequest:
        'KI-Chat has access to all documents in the request.',
      DropYourFileHereOr: 'Drop your files here or',
      browse: 'browse',
      Uploading: 'Uploading Documents',
      KIChatHasAccessToAllDataInTheRequest: 'KI-Chat has access to all data in the request.',
      Requester: 'Requester',
      Notary: 'Notary',
      Opponent: 'Opponent',
      Employer: 'Employer',
      UnknownRole: 'Unknown Role',
      context: 'Context'
    },
    de: {
      ChatDescription: 'Ohne Nachrichten',
      Document: 'Dokument',
      Documents: 'Dokumente',
      Participant: 'Beteiligter',
      Participants: 'Beteiligte',
      Ready: 'Bereit',
      UpdatingContext: 'Aktualisiere Kontext',
      SearchingDocuments: 'Durchsuche Dokumente',
      Thinking: 'Denke nach',
      Hello: 'Hallo',
      HowCanIHelpYou: 'wie kann ich Ihnen helfen?',
      ChooseQuestionTemplate:
        'Wählen Sie eine der folgenden Fragevorlagen oder stellen Sie Ihre eigene Frage.',
      Page: 'Seite',
      Pages: 'Seiten',
      KIChatHasAccessToAllDocumentsInTheRequest:
        'Der KI-Chat hat Zugriff auf alle Dokumente in dieser Anfrage.',
      DropYourFileHereOr: 'Legen Sie Ihre Datei hier ab oder',
      browse: 'durchsuchen',
      Uploading: 'Dokumente werden hochgeladen',
      KIChatHasAccessToAllDataInTheRequest:
        'Der KI-Chat hat Zugriff auf alle Daten in dieser Anfrage.',
      Requester: 'Antragsteller',
      Notary: 'Notar',
      Opponent: 'Gegner',
      Employer: 'Arbeitgeber',
      UnknownRole: 'Unbekannte Rolle',
      context: 'Kontext'
    }
  })

  const activeChatSession = inject<Ref<AIChatSession | null>>('activeChatSession', ref(null))
  const isAnySessionGenerating = inject(
    'isAnySessionGenerating',
    computed(() => false)
  )
  const isSessionGenerating = inject<(uuid: string) => boolean>('isSessionGenerating', () => false)
  const isActiveSessionGenerating = computed(() => {
    return activeChatSession.value && isSessionGenerating(activeChatSession.value.uuid)
  })
  const activeChatSessionUser = computed<CustomerOrganisationUser | null>(() => {
    return (
      caseData.value?.org.users.find(
        (user) => user.uuid === activeChatSession.value?.started_by_user
      ) || null
    )
  })
  const currentUser = inject<Ref<CustomerUser | null>>('currentUser', ref(null))
  const messagesContainer = ref<HTMLElement | null>(null)
  const messages = computed(() => activeChatSession.value?.messages)
  const sendChatMessage = inject(
    'sendChatMessage',
    async (session: AIChatSession, message: string) => {}
  )
  const isDocumentPanelOpen = inject<Ref<boolean>>('isDocumentPanelOpen', ref(false))
  const toggleDocumentPanel = inject<() => void>('toggleDocumentPanel')
  const showSessionStateProgress = computed(
    () => activeChatSession.value?.session_state !== SessionStateEnum.READY
  )
  const caseData = inject<Ref<CustomerCase | null>>('caseData', ref(null))
  const stateMessage = computed(() => {
    const stateMessages: Record<SessionStateEnum, string> = {
      [SessionStateEnum.READY]: t('Ready'),
      [SessionStateEnum.UPDATING_CONTEXT]: t('UpdatingContext'),
      [SessionStateEnum.SEARCHING_DOCUMENTS]: t('SearchingDocuments'),
      [SessionStateEnum.REASONING]: t('Thinking')
    }
    const currentState = activeChatSession.value?.session_state as SessionStateEnum
    return currentState ? stateMessages[currentState] : ''
  })

  const lastUserMessage = computed(() => {
    if (!activeChatSession.value?.messages) return ''
    const userMessages = activeChatSession.value.messages
      .filter((msg) => msg.role === 'user')
      .sort((a, b) => new Date(b.created_at || '') - new Date(a.created_at || ''))
    return userMessages[0]?.content || ''
  })

  const truncatedLastMessage = computed(() => {
    const msg = lastUserMessage.value
    return msg.length > 50 ? msg.slice(0, 50) + '...' : msg
  })

  const scrollToBottom = async () => {
    await nextTick()
    if (messagesContainer.value) {
      console.log('scrolling to bottom')
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  }

  watch(
    () => messages.value,
    () => {
      console.log('messages changed')
      scrollToBottom()
    },
    { deep: true }
  )

  onMounted(() => {
    scrollToBottom()
  })

  const { loadCase } = useCaseStore()

  // Handle document AI ready (first stage of processing)
  const onFileAiReady = async () => {
    // Check if all documents are processed before setting to false
    if (documentsBeingProcessed.value <= 1) {
      isDocumentProcessing.value = false
      // Show success message when processing is complete
      showSuccessMessage.value = true

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        showSuccessMessage.value = false
      }, 5000)
    } else {
      documentsBeingProcessed.value--
    }

    if (caseData.value?.uuid) {
      await loadCase(caseData.value.uuid)
    }
  }

  const getThumbnailUrl = (doc: any) => {
    if (!doc.images?.length) return null
    const thumbnail = doc.images.find((img: any) => img.image_preset === 'thumbnail')
    return thumbnail?.url || null
  }

  const truncateFilename = (filename: string, maxLength: number = 24) => {
    if (!filename || filename.length <= maxLength) return filename

    const extension = filename.split('.').pop()
    const name = filename.substring(0, filename.lastIndexOf('.'))

    const truncatedLength = maxLength - extension.length - 3 // -3 for "..." and "."
    const truncatedName = name.substring(0, truncatedLength) + '...'

    return `${truncatedName}.${extension}`
  }

  // TODO: inspect if still used
  const downloadDocument = (doc: any) => {
    if (!doc.url) return

    // Create a temporary anchor element
    const link = document.createElement('a')
    link.href = doc.url
    link.download = doc.filename // This will suggest the original filename

    // Append to body, click, and remove
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const ROLE_MAPPING = {
    'entities.labor_law_employer': 'Arbeitgeber',
    'entities.labor_law_employee': 'Arbeitnehmer',
    'entities.opponent': 'Gegner',
    'entities.client': 'Mandant'
  }

  const formatEntityName = (entity) => {
    if (entity.entity_type === EntityTypeEnum.NATURAL_PERSON) {
      return `${entity.first_name} ${entity.last_name}`
    }
    if (entity.entity_type === EntityTypeEnum.LEGAL_PERSON) {
      return `${entity.company_name} ${entity.company_legal_form}`
    }
    return entity.company_name
  }

  const handleEntityClick = (entity, element) => {
    const entityWithName = {
      ...entity,
      name: formatEntityName(entity)
    }
    showCard(entityWithName, element)
  }

  const { showCard } = useEntityCard()

  // Number of templates to show initially
  const initialTemplateCount = 6

  // Track if we're showing all templates
  const showingAllTemplates = ref(false)

  const isPromptPopoverOpen = ref(false)
  provide('isPromptPopoverOpen', isPromptPopoverOpen)

  // Function to open the prompt popover
  const openPromptPopover = () => {
    isPromptPopoverOpen.value = true
  }

  // Compute the templates to display
  const visibleTemplates = computed(() => {
    const allTemplates = caseData.value?.org?.ai_chat_question_templates || []

    if (showingAllTemplates.value || allTemplates.length <= initialTemplateCount) {
      return allTemplates
    }

    return allTemplates.slice(0, initialTemplateCount)
  })

  // Calculate if there are more templates to show
  const hasMoreTemplates = computed(() => {
    const allTemplates = caseData.value?.org?.ai_chat_question_templates || []
    return !showingAllTemplates.value && allTemplates.length > initialTemplateCount
  })

  // Calculate how many templates are hidden
  const remainingTemplatesCount = computed(() => {
    const allTemplates = caseData.value?.org?.ai_chat_question_templates || []
    return allTemplates.length - initialTemplateCount
  })

  // Function to show all templates
  const showAllTemplates = () => {
    showingAllTemplates.value = true
  }

  const isDocumentProcessing = ref(false)
  const documentsBeingProcessed = ref(0)
  const showSuccessMessage = ref(false)

  const onDocumentsProcessingStart = (count: number) => {
    isDocumentProcessing.value = true
    documentsBeingProcessed.value = count || 1
    // Hide success message when processing starts
    showSuccessMessage.value = false
  }
</script>

<template>
  <div class="flex h-full flex-1 flex-col">
    <div class="flex h-full flex-col">
      <div class="relative flex h-full flex-col overflow-hidden">
        <div class="flex h-full flex-col overflow-hidden">
          <header
            class="sticky top-0 z-[9] flex items-center justify-between gap-x-2 rounded-b-none rounded-r-lg border-b
              border-gray-200 bg-white p-2 dark:border-neutral-700 dark:bg-neutral-800 sm:px-5"
          >
            <!-- User Info -->
            <div>
              <button
                type="button"
                class="flex items-center gap-x-3.5 truncate focus:outline-none"
              >
                <UserAvatar
                  v-if="activeChatSessionUser || currentUser"
                  :user="activeChatSessionUser || currentUser"
                  size="32px"
                />
                <span class="grow truncate text-center lg:text-start">
                  <span
                    class="block truncate text-sm font-semibold leading-4 text-gray-800 dark:text-neutral-200"
                  >
                    {{ (activeChatSessionUser || currentUser)?.first_name }}
                    {{ (activeChatSessionUser || currentUser)?.last_name }}
                  </span>
                  <span
                    class="block truncate text-xs leading-4 text-gray-500 dark:text-neutral-500"
                  >
                    {{ truncatedLastMessage || t('ChatDescription') }}
                  </span>
                </span>
              </button>
            </div>

            <div class="flex items-center gap-3 text-gray-500 dark:text-neutral-400">
              <span class="text-xs">{{ t('context') }}:</span>
              <!-- Documents -->
              <DocumentsPopover
                :case-data="caseData"
                @file-ai-ready="onFileAiReady"
                @documents-processing-start="onDocumentsProcessingStart"
              />
              <!-- Participants -->
              <ParticipantsPopover :case-data="caseData" />
            </div>
          </header>

          <div class="flex flex-1 overflow-hidden">
            <div class="flex flex-1 flex-col overflow-hidden transition-all duration-300">
              <div
                ref="messagesContainer"
                class="flex-1 overflow-y-auto"
              >
                <div class="space-y-5 p-6">
                  <div
                    v-if="!messages?.length"
                    class="flex flex-col items-center justify-center px-4"
                  >
                    <h2 class="pt-6 text-center text-3xl font-semibold tracking-tight sm:text-4xl">
                      👋
                      <div class="mt-4">
                        {{ t('Hello') }} {{ currentUser?.first_name }} {{ currentUser?.last_name }},
                        <br />
                        <span
                          class="bg-gradient-to-r from-pink-600 via-red-500 to-purple-600 bg-clip-text text-transparent"
                        >
                          {{ t('HowCanIHelpYou') }}
                        </span>
                      </div>
                    </h2>
                    <p class="mt-4 text-gray-600 dark:text-gray-400">
                      {{ t('ChooseQuestionTemplate') }}
                    </p>
                  </div>
                  <div
                    v-if="!messages?.length"
                    class="mt-8"
                  >
                    <!-- Templates Grid -->
                    <div class="flex flex-col items-center gap-3 px-4 md:px-8">
                      <div class="flex max-w-md flex-wrap justify-center gap-3">
                        <button
                          v-for="template in visibleTemplates"
                          :key="template.uuid"
                          class="inline-flex items-center gap-x-1.5 rounded-full border border-purple-100 bg-purple-50/50 px-3 py-1.5
                            text-sm transition-colors hover:border-purple-200 hover:bg-purple-50 dark:border-purple-900/30
                            dark:bg-purple-900/10 dark:hover:border-purple-900/50 dark:hover:bg-purple-900/20"
                          @click="sendChatMessage(activeChatSession, template.prompt)"
                        >
                          <span
                            v-if="template.law_field"
                            class="font-medium text-purple-900 dark:text-purple-100"
                          >
                            {{ template.law_field }}:
                          </span>
                          <span
                            :class="[
                              template.law_field
                                ? 'text-purple-700'
                                : 'font-medium text-purple-900',
                              'dark:text-purple-300'
                            ]"
                          >
                            {{ template.title }}
                          </span>
                        </button>
                        <!-- Show More Link -->
                        <button
                          v-if="hasMoreTemplates"
                          class="inline-flex items-center rounded-full px-3 py-1.5 text-sm font-medium text-purple-600
                            hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300"
                          @click="openPromptPopover"
                        >
                          + {{ remainingTemplatesCount }}
                          {{ remainingTemplatesCount === 1 ? 'template' : 'templates' }} more
                        </button>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="messages?.length > 0"
                    class="mt-6 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-gray-700
                      dark:border-yellow-900/30 dark:bg-yellow-900/20 dark:text-gray-200"
                  >
                    <h4 class="mb-2 font-medium">KI Hinweis</h4>
                    <p class="mb-1">
                      Die Antworten von JUPUS werden von
                      <span class="font-bold">künstlicher Intelligenz</span>
                      generiert, die dazu entwickelt wurde, die Arbeit mit Ihren Dokumenten zu
                      vereinfachen und nicht für die Übernahme der kernjuristischen Tätigkeit.
                    </p>
                    <p class="mb-1">
                      JUPUS hat daher (noch) keinen Zugriff auf Urteilsdatenbanken oder
                      Fachliteratur. Von der KI zitierte Gerichtsurteile oder Literaturnennungen
                      könnten erfunden sein.
                    </p>
                    <p class="mb-1">
                      Die
                      <span class="font-bold">Verarbeitung und Generierung</span>
                      aller Inhalte erfolgt
                      <span class="font-bold">automatisiert</span>
                      .
                    </p>
                    <p class="mb-1">
                      <span class="font-bold">Ungenauigkeiten</span>
                      oder
                      <span class="font-bold">Fehlinterpretationen</span>
                      können gelegentlich auftreten.
                    </p>
                    <p>
                      Bei Rückfragen, Problemen oder Verbesserungsvorschlägen melden Sie sich per
                      E-Mail an
                      <a
                        href="mailto:support@jupus.de"
                        class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        support@jupus.de
                      </a>
                    </p>
                  </div>
                  <AiChatMessage
                    v-for="message in messages"
                    :key="message.uuid"
                    :message="message"
                  />
                  <div
                    v-if="isActiveSessionGenerating"
                    class="flex gap-x-2 sm:gap-x-4"
                  >
                    <div class="w-full max-w-[90%] grow space-y-3 md:max-w-2xl">
                      <div
                        class="space-y-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-900"
                      >
                        <div class="text-sm text-gray-800 dark:text-white">
                          <div class="space-y-4">
                            <div
                              v-if="showSessionStateProgress"
                              class="flex items-center gap-2 text-xs text-gray-500"
                            >
                              <div class="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500"></div>
                              {{ stateMessage }}
                            </div>
                            <div class="space-y-2">
                              <SkeletonLoader class="h-4 w-3/4" />
                              <SkeletonLoader class="h-4 w-1/2" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Message input component -->
              <AiChatMessageInput />
            </div>
          </div>
        </div>
      </div>
    </div>
    <OffCanvas
      v-model="isDocumentPanelOpen"
      position="right"
      width="w-80"
    >
      <template #title>
        {{ caseData?.documents?.length === 1 ? t('Document') : t('Documents') }}
      </template>

      <div
        v-for="document in caseData?.documents"
        :key="document.uuid"
        class="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2 dark:border-neutral-700
          dark:bg-neutral-800"
      >
        <div
          class="relative h-10 w-10 shrink-0 overflow-hidden rounded-md border border-gray-200
            dark:border-neutral-700"
        >
          <img
            v-if="document.images?.length"
            :src="getThumbnailUrl(document)"
            :alt="document.title"
            class="h-full w-full object-cover"
          />
          <div
            v-else
            class="flex h-full w-full items-center justify-center bg-gray-100 text-gray-400 dark:bg-neutral-700
              dark:text-neutral-500"
          >
            <FileIcon class="h-5 w-5" />
          </div>
        </div>

        <div class="min-w-0 flex-1">
          <h4 class="truncate text-sm font-medium text-gray-900 dark:text-white">
            {{ document.title }}
          </h4>
          <div class="mt-0.5 flex items-center gap-2 text-xs text-gray-500 dark:text-neutral-400">
            <span>
              {{
                new Date(document.created_at).toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                })
              }}
            </span>
            <span>•</span>
            <span>
              {{
                document.page_count === 1
                  ? `1 ${t('Page')}`
                  : `${document.page_count} ${t('Pages')}`
              }}
            </span>
          </div>
        </div>
      </div>
      <div class="upload-section">
        <DocumentUpload
          :case-data="caseData"
          :auto-remove-after-ai-ready="true"
          :show-processing-documents="true"
          @file-ai-ready="onFileAiReady"
        />
      </div>
    </OffCanvas>
  </div>
</template>

<style>
  .slide-enter-active,
  .slide-leave-active {
    transition: transform 0.2s ease-out;
  }

  .slide-enter-from,
  .slide-leave-to {
    transform: translateX(100%);
  }

  .slide-enter-to,
  .slide-leave-from {
    transform: translateX(0);
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
  }

  .upload-section {
    max-height: 120px;
    overflow-y: auto;
    background-color: var(--background);
    border-radius: 4px;
  }

  .upload-section:empty {
    display: none;
  }
</style>
